import React from 'react'
import {
  Divider,
  MetricCard,
  StatsCard,
  Rating,
  SectionWithContent,
} from 'components/NewCompanyPage/Sidebar/shared'
import { formatInteger } from 'utils/formatting'

interface GooglePlaceData {
  id?: string | number
  name?: string
  domain?: string
  reviews_avg?: number
  reviews_cnt?: number
  doors_cnt?: number
  reviews_per_score?: Record<string, number>
  city_summary?: string[]
  state_summary?: string[]
  zipcode_summary?: string[]
  open_hours?: object
}

/**
 * Build stats array for the summary tab
 */
export function buildGooglePlaceStats(placeData: GooglePlaceData | undefined) {
  const stats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Average Rating',
      value: placeData?.reviews_avg ? (
        <Rating value={Number(placeData.reviews_avg.toFixed(2))} />
      ) : (
        '-'
      ),
    },
    {
      label: 'Total Reviews',
      value: placeData?.reviews_cnt
        ? formatInteger(placeData.reviews_cnt)
        : '0',
    },
    {
      label: 'Door Count',
      value: placeData?.doors_cnt ? formatInteger(placeData.doors_cnt) : '0',
    },
  ]
  return stats
}

/**
 * Build review breakdown stats from reviews_per_score
 */
export function buildReviewBreakdownStats(
  placeData: GooglePlaceData | undefined
) {
  const reviewBreakdownStats: Array<{ label: string; value: React.ReactNode }> =
    []

  if (placeData?.reviews_per_score) {
    const scores = Object.keys(placeData.reviews_per_score).sort().reverse()
    scores.forEach((score) => {
      reviewBreakdownStats.push({
        label: `${score} Star Reviews`,
        value: formatInteger(placeData.reviews_per_score![score]),
      })
    })
  }

  return reviewBreakdownStats
}

/**
 * Build location coverage stats for location details tab
 */
export function buildLocationCoverageStats(
  placeData: GooglePlaceData | undefined
) {
  const locationSummaries: Array<{ label: string; value: React.ReactNode }> = []

  if (placeData?.city_summary && placeData.city_summary.length > 0) {
    locationSummaries.push({
      label: 'City Coverage',
      value: placeData.city_summary.join(', ') || 'N/A',
    })
  }

  if (placeData?.state_summary && placeData.state_summary.length > 0) {
    locationSummaries.push({
      label: 'State Coverage',
      value: placeData.state_summary.join(', ') || 'N/A',
    })
  }

  if (placeData?.zipcode_summary && placeData.zipcode_summary.length > 0) {
    locationSummaries.push({
      label: 'ZIP Code Coverage',
      value: placeData.zipcode_summary.join(', ') || 'N/A',
    })
  }

  return locationSummaries
}

/**
 * Build the summary tab content for Google Places pages
 */
export function buildGooglePlaceSummaryContent({
  onViewDoorsClick,
  placeData,
  hideDoorsMetric = false,
}: {
  placeData: GooglePlaceData | undefined
  onViewDoorsClick: () => void
  hideDoorsMetric?: boolean
}): React.ReactNode[] {
  const summaryTabContent: React.ReactNode[] = []

  if (!hideDoorsMetric) {
    // Door Count Metric
    summaryTabContent.push(
      <MetricCard
        key="doors"
        value={formatInteger(placeData?.doors_cnt || 0)}
        label="Total Locations"
        actionText="View Doors"
        onActionClick={onViewDoorsClick}
      />
    )
  }

  // Main Stats
  const placeStats = buildGooglePlaceStats(placeData)
  summaryTabContent.push(<StatsCard key="stats" stats={placeStats} />)

  // Review Breakdown if available
  const reviewBreakdownStats = buildReviewBreakdownStats(placeData)
  if (reviewBreakdownStats.length > 0) {
    summaryTabContent.push(<Divider key="divider-1" />)
    summaryTabContent.push(
      <StatsCard key="review-breakdown" stats={reviewBreakdownStats} />
    )
  }

  return summaryTabContent
}

/**
 * Build the location details tab content for Google Places pages
 */
export function buildGooglePlaceLocationContent(
  placeData: GooglePlaceData | undefined
): React.ReactNode[] {
  const locationDetailsTabContent: React.ReactNode[] = []

  // Location summaries using SectionWithContent (like chains)
  locationDetailsTabContent.push(
    <div key="summaries" className="space-y-9">
      <SectionWithContent
        key="city-summary"
        title="City Summary"
        content={
          placeData?.city_summary && placeData.city_summary.length > 0
            ? placeData.city_summary.join(', ')
            : 'N/A'
        }
      />
      <SectionWithContent
        key="state-summary"
        title="State Summary"
        content={
          placeData?.state_summary && placeData.state_summary.length > 0
            ? placeData.state_summary.join(', ')
            : 'N/A'
        }
      />
      <SectionWithContent
        key="zip-summary"
        title="ZIP Summary"
        content={
          placeData?.zipcode_summary && placeData.zipcode_summary.length > 0
            ? placeData.zipcode_summary.join(', ')
            : 'N/A'
        }
      />
    </div>
  )

  return locationDetailsTabContent
}

/**
 * Configuration for different Google Places types
 */
export interface GooglePlaceConfig {
  breadcrumbRoot: string
  breadcrumbTitle: string
  modalSlug: string
  modalName: string
}

export const GOOGLE_PLACE_CONFIGS: Record<string, GooglePlaceConfig> = {
  lodging: {
    breadcrumbRoot: 'Lodging & Hospitality',
    breadcrumbTitle: 'Lodging',
    modalSlug: 'lodging-hospitality',
    modalName: 'Lodging & Hospitality',
  },
  convenience: {
    breadcrumbRoot: 'Convenience Stores',
    breadcrumbTitle: 'Convenience',
    modalSlug: 'convenience',
    modalName: 'Convenience',
  },
}
